import React, { useEffect, useState, useRef } from 'react';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import BusinessUnitService from '../../services/businessUnitService';
import { showConfirm, showSuccessDetail, showDeleteDetail, showError, showLoading, hideLoading } from '../../components/utils/swal-helpers';

// ─────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────

const ROWS_PER_PAGE = 8;

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────

/** คำนวณ page numbers ที่แสดงรอบ current page (±2) */
const getPageNumbers = (currentPage, totalPages) => {
    const start = Math.max(1, currentPage - 2);
    const end   = Math.min(totalPages, currentPage + 2);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

// ─────────────────────────────────────────────────────────
// Company Modal
// ─────────────────────────────────────────────────────────

const CompanyModal = ({ mode, data, onClose, onSave }) => {
    const [form, setForm] = useState({
        code:      data?.code    || '',
        name_th:   data?.name_th || '',
        name_en:   data?.name_en || '',
        domain:    data?.domain  || '',
        // can_login จาก API เป็น int (0/1) แปลงเป็น boolean สำหรับ checkbox
        can_login: data?.can_login == 1 || false,
    });

    const isEdit = mode === 'edit';

    const handleChange = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleSave = () => {
        // แปลง can_login boolean → string '1'/'0' ตาม API spec
        setTimeout(() => onSave({ ...form, can_login: form.can_login ? '1' : '0' }), 0);
    };

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mc-modal-header">
                    <h3>{isEdit ? 'แก้ไข Edit' : 'เพิ่มบริษัทใหม่ New Company'}</h3>
                </div>
                <div className="mc-modal-body">
                    <div className="border-line">
                        <div className="mc-form-row">
                            <div className="mc-form-group">
                                <label>Code</label>
                                <input
                                    type="text"
                                    placeholder="Input"
                                    value={form.code}
                                    onChange={(e) => handleChange('code', e.target.value)}
                                />
                            </div>
                            <div className="mc-form-group">
                                <label>Domain</label>
                                <input
                                    type="text"
                                    placeholder="Input"
                                    value={form.domain}
                                    onChange={(e) => handleChange('domain', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="mc-form-row">
                            <div className="mc-form-group">
                                <label>Name (TH)</label>
                                <input
                                    type="text"
                                    placeholder="Input"
                                    value={form.name_th}
                                    onChange={(e) => handleChange('name_th', e.target.value)}
                                />
                            </div>
                            <div className="mc-form-group">
                                <label>Name (EN)</label>
                                <input
                                    type="text"
                                    placeholder="Input"
                                    value={form.name_en}
                                    onChange={(e) => handleChange('name_en', e.target.value)}
                                />
                            </div>
                        </div>
                        {/* <div className="mc-form-check">
                            <input
                                type="checkbox"
                                id="allow-login"
                                checked={form.can_login}
                                onChange={(e) => handleChange('can_login', e.target.checked)}
                            />
                            <label htmlFor="allow-login">Allow Login</label>
                        </div> */}
                    </div>
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn mc-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="mc-btn mc-btn--save" onClick={handleSave}>
                        {isEdit ? 'Save' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────

const AdminCompany = () => {
    // ── Data state ──
    const [data, setData]     = useState([]);
    const [search, setSearch] = useState('');
    const [modal, setModal]   = useState(null); // null | { mode, data? }
    const [newId, setNewId]   = useState(null); // id ของ row ที่เพิ่งสร้าง (สำหรับ highlight)

    // ── Pagination state ──
    const [currentPage, setCurrentPage] = useState(1);

    // ── Refs ──
    const highlightRef = useRef(null);

    // โหลดข้อมูลตอน mount และ clear timeout เมื่อ unmount
    useEffect(() => {
        loadData();
        return () => { if (highlightRef.current) clearTimeout(highlightRef.current); };
    }, []);

    // ─────────────────────────────────────────────────────
    // Data loaders
    // ─────────────────────────────────────────────────────

    const loadData = async () => {
        try {
            const resp = await BusinessUnitService.SearchBusinessUnit({ status: 1 });
            if (!resp.data.is_success || !resp.data.result) return;
            // sort A-Z ตามชื่อภาษาอังกฤษ
            const sorted = [...resp.data.result].sort((a, b) =>
                (a.name_en || '').localeCompare(b.name_en || '', 'en')
            );
            setData(sorted);
        } catch (err) { console.error(err); }
    };

    // ─────────────────────────────────────────────────────
    // Handlers
    // ─────────────────────────────────────────────────────

    const handleSave = async (form) => {
        const { mode: currentMode, data: currentData } = modal;
        setModal(null);

        const confirmed = await showConfirm({
            title: currentMode === 'edit' ? 'ยืนยันการแก้ไขบริษัท' : 'ยืนยันการเพิ่มบริษัทใหม่',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;

        try {
            showLoading();
            const payload = {
                code:      form.code,
                name_th:   form.name_th,
                name_en:   form.name_en || form.name_th,
                domain:    form.domain  || '',
                // แปลง boolean กลับเป็น int สำหรับ API
                can_login: form.can_login ? 1 : 0,
                status:    1,
            };

            let createdId = null;
            if (currentMode === 'edit') {
                await BusinessUnitService.UpdateBusinessUnit(currentData?.id, payload);
            } else {
                const resp = await BusinessUnitService.CreateBusinessUnit(payload);
                createdId = resp.data?.result?.id || resp.data?.result?.[0]?.id || null;
            }

            hideLoading();
            await showSuccessDetail({
                icon: 'mdi mdi-check-circle',
                title: currentMode === 'edit' ? 'แก้ไขบริษัทสำเร็จแล้ว' : 'เพิ่มบริษัทสำเร็จแล้ว',
            });

            await loadData();

            // highlight row ที่เพิ่งสร้างใหม่เป็นเวลา 10 วิ
            if (currentMode === 'add' && createdId) {
                setNewId(createdId);
                if (highlightRef.current) clearTimeout(highlightRef.current);
                highlightRef.current = setTimeout(() => setNewId(null), 10000);
            }
        } catch (err) { hideLoading(); showError(); }
    };

    const handleDelete = async (row) => {
        const confirmed = await showConfirm({
            title: 'แน่ใจใช่ไหมว่าต้องการลบ',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;
        try {
            showLoading();
            await BusinessUnitService.DeleteBusinessUnit(row.id);
            hideLoading();
            await showDeleteDetail({ title: 'ลบบริษัทออกแล้ว' });
            loadData();
        } catch (err) { hideLoading(); showError(); }
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1); // reset หน้าเมื่อ search เปลี่ยน
    };

    // ─────────────────────────────────────────────────────
    // Derived data (filter + pagination)
    // ─────────────────────────────────────────────────────

    const filtered = data.filter((row) => {
        const q = search.toLowerCase();
        if (!q) return true;
        return (
            (row.code    || '').toLowerCase().includes(q) ||
            (row.name_th || '').toLowerCase().includes(q) ||
            (row.name_en || '').toLowerCase().includes(q) ||
            (row.domain  || '').toLowerCase().includes(q)
        );
    });

    const totalPages  = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
    const safePage    = Math.min(currentPage, totalPages);
    const paged       = filtered.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);
    const pageNumbers = getPageNumbers(safePage, totalPages);

    const goPage = (p) => {
        if (p >= 1 && p <= totalPages) setCurrentPage(p);
    };

    const countLabel = filtered.length === 0
        ? '0 รายการ'
        : `${(safePage - 1) * ROWS_PER_PAGE + 1}-${Math.min(safePage * ROWS_PER_PAGE, filtered.length)} จาก ${filtered.length} รายการ`;

    // ─────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────

    return (
        <>
            <div className="mc-page">
                {/* ── Header ── */}
                <div className="mc-header">
                    <div className="mc-header__left">
                        <h2 className="mc-title">Master Data - Company</h2>
                        <p className="mc-subtitle">รายชื่อบริษัท</p>
                    </div>
                    <button className="mc-btn mc-btn--new" onClick={() => setModal({ mode: 'add' })}>
                        <i className="ri-add-line" /> New Company
                    </button>
                </div>

                <div className="mc-card">
                    {/* ── Toolbar: search + count ── */}
                    <div className="mc-toolbar">
                        <div className="mc-search">
                            <i className="ri-search-line" />
                            <input
                                type="text"
                                placeholder="Select"
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </div>
                        <div className="mc-toolbar__right">
                            <span className="mc-count">{countLabel}</span>
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div style={{ overflowX: 'auto' }}>
                        <table className="mc-table">
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>Code</th>
                                    <th>Name TH</th>
                                    <th>Name EN</th>
                                    <th>Domain</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="mc-table-empty">ไม่มีข้อมูล</td>
                                    </tr>
                                ) : (
                                    paged.map((row, idx) => (
                                        <tr key={row.id} className={row.id === newId ? 'mc-row-new' : ''}>
                                            <td className="mc-col-no">{(safePage - 1) * ROWS_PER_PAGE + idx + 1}</td>
                                            <td className="mc-col-code">{row.code}</td>
                                            <td>{row.name_th}</td>
                                            <td className="mc-col-muted">{row.name_en || '-'}</td>
                                            <td className="mc-col-muted">{row.domain  || '-'}</td>
                                            <td>
                                                <div className="mc-actions">
                                                    <button
                                                        className="mc-action-btn mc-action-btn--edit"
                                                        onClick={() => setModal({ mode: 'edit', data: row })}
                                                    >
                                                        <i className="ri-pencil-line" />
                                                    </button>
                                                    <button
                                                        className="mc-action-btn mc-action-btn--delete"
                                                        onClick={() => handleDelete(row)}
                                                    >
                                                        <i className="ri-delete-bin-line" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination ── */}
                    <div className="table-pagination">
                        <Pagination>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink first onClick={() => goPage(1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink previous onClick={() => goPage(safePage - 1)} />
                            </PaginationItem>
                            {pageNumbers.map((p) => (
                                <PaginationItem key={p} active={p === safePage}>
                                    <PaginationLink onClick={() => goPage(p)}>{p}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink next onClick={() => goPage(safePage + 1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink last onClick={() => goPage(totalPages)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                </div>
            </div>

            {/* ── Modal ── */}
            {modal && (
                <CompanyModal
                    mode={modal.mode}
                    data={modal.data}
                    onClose={() => setModal(null)}
                    onSave={handleSave}
                />
            )}
        </>
    );
};

export default AdminCompany;